/**
 * API client — typed wrappers around the BossBattle REST API.
 * Base URL is set via VITE_API_URL (defaults to http://localhost:3001).
 */

// In production the API is served from the same origin as the frontend, so the
// base is empty ('' → relative /api/... requests). In dev we point at the local
// API server. An explicit VITE_API_URL always wins.
const BASE = (import.meta.env.VITE_API_URL ?? (import.meta.env.PROD ? '' : 'http://localhost:3001')) as string

let _token: string | null = null
export function setToken(t: string | null) { _token = t }
export function getToken() { return _token }

// Called when an authenticated request comes back 401 (expired/invalid token),
// e.g. a student reopening yesterday's tab. GameContext registers logout() here
// so the app bounces to the login screen instead of rendering an empty world.
let _onUnauthorized: (() => void) | null = null
export function setOnUnauthorized(fn: (() => void) | null) { _onUnauthorized = fn }

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (_token) headers['Authorization'] = `Bearer ${_token}`

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    // Expired/invalid session (only when we actually sent a token — a failed
    // *login* is also 401 but shouldn't trigger the logout path)
    if (res.status === 401 && _token) _onUnauthorized?.()
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }

  // For CSV downloads
  const ct = res.headers.get('content-type') || ''
  if (ct.includes('text/csv')) return res.text() as unknown as T

  return res.json() as T
}

const get  = <T>(path: string)              => request<T>('GET',    path)
const post = <T>(path: string, body: unknown) => request<T>('POST',   path, body)
const put  = <T>(path: string, body: unknown) => request<T>('PUT',    path, body)
const patch= <T>(path: string, body: unknown) => request<T>('PATCH',  path, body)
const del  = <T>(path: string)              => request<T>('DELETE', path)

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Standard {
  id: number
  standard_id: string
  grade: number
  strand: string
  biome: string
  full_text: string
  player_summary: string
  boss_name: string
  boss_theme: string
  prereq_ids: string[]
  mstep_avg_pct: number | null
  world_x: number
  world_y: number
  sort_order: number
}

export interface Question {
  id: number
  standard_id: string
  school_id: number | null
  type: string
  prompt: string
  scenario: string | null
  options_json: unknown
  correct_json: unknown
  hint: string | null
  points: number
  difficulty: number
  is_active: boolean
}

export interface Attempt {
  id: number
  student_id: number
  standard_id: string
  session_label: string
  score: number
  max_score: number
  pct_correct: number
  mastery_status: string
  items: unknown[]
  completed_at: number | null
}

export interface StudentInfo {
  id: number
  display_name: string
  grade: number
  xp: number
  level: number
  avatar: string
}

export interface ClassInfo {
  id: number
  name: string
  grade: number
  session_label: string
  /** 1 = only names already on the roster can log in (teacher toggles this after importing). */
  roster_locked?: number
}

export interface TeacherInfo {
  id: number
  name: string
  email: string
  role: string
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export const auth = {
  studentLogin: (class_code: string, display_name: string) =>
    post<{ token: string; student: StudentInfo; class: ClassInfo }>(
      '/api/auth/student', { class_code, display_name },
    ),

  teacherLogin: (email: string, password: string) =>
    post<{ token: string; teacher: TeacherInfo; school: { id: number; name: string; subscription: string } }>(
      '/api/auth/teacher', { email, password },
    ),

  changePassword: (email: string, current_password: string, new_password: string) =>
    post<{ ok: true }>('/api/auth/teacher/change-password', { email, current_password, new_password }),
}

// ── Standards ─────────────────────────────────────────────────────────────────

export const standards = {
  list: (params?: { grade?: number; biome?: string; strand?: string }) => {
    const qs = new URLSearchParams()
    if (params?.grade)  qs.set('grade',  String(params.grade))
    if (params?.biome)  qs.set('biome',  params.biome)
    if (params?.strand) qs.set('strand', params.strand)
    return get<Standard[]>(`/api/standards?${qs}`)
  },
  get: (standardId: string) => get<Standard>(`/api/standards/${standardId}`),
}

// ── Questions ─────────────────────────────────────────────────────────────────

export const questions = {
  forBattle: (standard_id: string) =>
    get<Question[]>(`/api/questions?standard_id=${encodeURIComponent(standard_id)}`),

  manage: (standard_id?: string) => {
    const qs = standard_id ? `?standard_id=${encodeURIComponent(standard_id)}` : ''
    return get<Question[]>(`/api/questions/manage${qs}`)
  },

  create: (q: Omit<Question, 'id' | 'school_id' | 'is_active'>) =>
    post<Question>('/api/questions', q),

  update: (id: number, updates: Partial<Question>) =>
    put<Question>(`/api/questions/${id}`, updates),

  delete: (id: number) => del<{ ok: true }>(`/api/questions/${id}`),
}

// ── Attempts ──────────────────────────────────────────────────────────────────

export const attempts = {
  mine: (standard_id?: string) => {
    const qs = standard_id ? `?standard_id=${encodeURIComponent(standard_id)}` : ''
    return get<Attempt[]>(`/api/attempts/me${qs}`)
  },

  save: (data: {
    standard_id: string
    score: number
    max_score: number
    items?: unknown[]
    session_label?: string
    duration_s?: number
  }) => post<{ attempt: Attempt; xp_earned: number; total_xp: number; level: number; mastery: string }>(
    '/api/attempts', data,
  ),
}

// ── Classes ───────────────────────────────────────────────────────────────────

export const classes = {
  list: () => get<(ClassInfo & { student_count: number; class_code: string })[]>('/api/classes'),

  create: (data: { name: string; grade: number; section?: string; session_label?: string }) =>
    post<ClassInfo>('/api/classes', data),

  update: (id: number, data: Partial<ClassInfo>) => patch<ClassInfo>(`/api/classes/${id}`, data),

  students: (id: number) =>
    get<(StudentInfo & { attempt_count: number; last_active: number | null })[]>(
      `/api/classes/${id}/students`,
    ),

  importRoster: (id: number, students: { display_name: string; grade?: number }[]) =>
    post<{ inserted: number; total: number; skipped: string[] }>(`/api/classes/${id}/students/bulk`, { students }),
}

// ── Events (engagement telemetry) ─────────────────────────────────────────────
// Best-effort, batched, and silent on failure — telemetry must never break play.
// Queue flushes every few seconds, when it grows past a threshold, and (via
// keepalive fetch) when the tab is hidden or closed mid-session.

interface GameEvent {
  type: 'login' | 'session_open' | 'boss_start' | 'boss_complete' | 'boss_abandon' | 'question_answer'
  standard_id?: string
  meta?: Record<string, unknown>
}

const _eventQueue: GameEvent[] = []
let _flushTimer: ReturnType<typeof setTimeout> | null = null

function flushEvents(keepalive = false) {
  if (_flushTimer) { clearTimeout(_flushTimer); _flushTimer = null }
  if (_eventQueue.length === 0 || !_token) return
  const batch = _eventQueue.splice(0, 50)
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${_token}`,
  }
  fetch(`${BASE}/api/events`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ events: batch }),
    keepalive,
  }).catch(() => { /* drop — never surface telemetry errors to students */ })
}

export const events = {
  /** Queue an event; flushes automatically. */
  track(type: GameEvent['type'], data?: { standard_id?: string; meta?: Record<string, unknown> }) {
    _eventQueue.push({ type, ...data })
    if (_eventQueue.length >= 20) { flushEvents(); return }
    if (!_flushTimer) _flushTimer = setTimeout(() => flushEvents(), 5000)
  },
  /** Force an immediate send (e.g. boss_abandon on retreat/unload). */
  flush(keepalive = false) { flushEvents(keepalive) },
}

// Flush pending events when the tab hides/closes so end-of-class data isn't lost.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEvents(true)
  })
  window.addEventListener('pagehide', () => flushEvents(true))
}

// ── Feedback ──────────────────────────────────────────────────────────────────

export interface FeedbackEntry {
  id: number
  source: string
  standard_id: string | null
  rating: number | null
  text: string | null
  created_at: number
  student_name: string | null
  boss_name: string | null
}

export const feedback = {
  /** rating for student_boss: -1 too easy / 0 just right / 1 too hard */
  submit: (data: { source: string; standard_id?: string; rating?: number; text?: string }) =>
    post<{ id: number; ok: true }>('/api/feedback', data),

  list: (class_id: number, source?: string) => {
    const qs = source ? `&source=${encodeURIComponent(source)}` : ''
    return get<{
      feedback: FeedbackEntry[]
      boss_summary: Array<{
        standard_id: string; boss_name: string | null
        too_easy: number; just_right: number; too_hard: number; confused: number
      }>
    }>(`/api/feedback?class_id=${class_id}${qs}`)
  },
}

// ── Reports ───────────────────────────────────────────────────────────────────

export const reports = {
  mastery: (class_id: number, session_label?: string) => {
    const qs = session_label ? `&session_label=${session_label}` : ''
    return get<{
      class: ClassInfo
      session_label: string
      standards: Pick<Standard, 'standard_id' | 'boss_name' | 'strand' | 'grade'>[]
      students: Array<{
        student_id: number
        display_name: string
        xp: number
        level: number
        standards: Record<string, { mastery: string; pct: number; completed_at: number | null }>
      }>
    }>(`/api/reports/mastery?class_id=${class_id}${qs}`)
  },

  csv: (class_id: number, session_label?: string) => {
    const qs = session_label ? `&session_label=${session_label}` : ''
    return get<string>(`/api/reports/csv?class_id=${class_id}${qs}`)
  },

  engagement: (class_id: number) =>
    get<{
      class: { id: number; name: string }
      roster_count: number
      weekly_active_students: number
      weekly_series: Array<{ week: string; active_students: number; events: number }>
      bosses: Array<{
        standard_id: string; boss_name: string | null; strand: string | null
        starts: number; completes: number; abandons: number; students: number
        completion_rate: number | null; avg_duration_s: number | null
      }>
    }>(`/api/reports/engagement?class_id=${class_id}`),

  growth: (class_id: number, pre = 'pre_2026', post = 'post_2026') =>
    get<{
      growth: Array<{
        standard_id: string
        boss_name: string
        strand: string
        pre: { avg_pct: number; count: number; mastered: number } | null
        post: { avg_pct: number; count: number; mastered: number } | null
        delta: number | null
      }>
    }>(`/api/reports/growth?class_id=${class_id}&pre=${pre}&post=${post}`),
}
